<!-- Copyright 2025, Common Good Learning Tools LLC -->
 <template>
	<div class="k-asset-manager">
		<h2 class="k-page-title d-flex">
			<v-icon color="primary" class="mr-3" large @click="$emit('return')">fas fa-file-certificate</v-icon>
			HQIM Publishing Dashboard 
		</h2>

		<v-tabs v-model="tab" bg-color="primary" style="border-radius: 6px 6px 0 0;">
			<v-tab v-for="(item) in tabs" :key="item.key" class="k-tight-btn k-nocaps-btn" style="font-size:16px"><b>{{ item.text }}</b></v-tab>
			<!-- <v-btn class="k-tight-btn load-btn" color="primary" @click="load(tabs[tab])">Load/Reload {{ tabs[tab].text }}</v-btn> -->
		</v-tabs>

		<v-tabs-items v-model="tab" style="border-radius: 0 0 6px 6px;">
			<v-tab-item v-for="(tab) in tabs" :key="tab.key" class="table-wrapper">
				<div class="d-flex justify-content-center pa-3">
					<div class="d-flex justify-content-center flex-fill">
						<v-text-field
							v-model="search.term"
							prepend-inner-icon="fa fa-search"
							clear-icon="fa fa-times-circle"
							label="Search"
							background-color="#fff"
							class="pr-3"
							clearable
							single-line
							hide-details
							outlined
							dense
						/>
						
						<v-select
							v-model="search.filters.grade"
							:items="filter_grades"
							placeholder="All Grades"
							class="pr-3"
							solo outlined dense flat hide-details
							disabled
						/>
						
						<v-select
							v-model="search.filters.subject"
							:items="filter_subjects"
							placeholder="All Subjects"
							class="pr-3"
							solo outlined dense flat hide-details
							disabled
						/>
						
						<v-select
							v-model="search.filters.status"
							:items="filter_statuses"
							placeholder="All Statuses"
							solo outlined dense flat hide-details
						/>
					</div>
				</div>

				<AssetManagerTable
					:tab="tab"
					:headers="headers"
					:search="search"
					:ref="'table_' + tab.key"
					@show_status="show_status($event, tab.key)"
				/>
			</v-tab-item>
		</v-tabs-items>

		<ResourceCollectionItem v-if="item_to_show"
			:quick_look_only="true"
			:open_to_publish_status="true"
			:item="item_to_show.asset"
			:option_overrides="{edit:true}"
			@quick_look_closed="item_to_show = undefined"
			@edit_item_saved="asset_saved"
		/>
	</div>
 </template>

 <script>
import { mapState } from 'vuex'
import AssetManagerTable from './AssetManagerTable.vue'
import ResourceCollectionItem from '../resources/ResourceCollectionItem.vue'

export default {
	name: 'AssetManager',

	components: {AssetManagerTable, ResourceCollectionItem},

	data() {
		return {
			init: false,
			tab: 0,
			options: {
				groupBy: [],
				groupDesc: [],
				itemsPerPage: 100,
				multiSort: false,
				mustSort: false,
				page: 1,
				sortBy: [],
				sortDesc: [false]
			},
			footerProps: {
				'items-per-page-options': [100, 50, 25, 10]
			},
			status: {
				show: false,
				item: undefined,
				type: undefined,
				roles: undefined
			},
			selected: [],
			resource_showing: undefined,
			resource_being_edited: undefined,
			lesson_being_edited: undefined,
			lesson_showing: undefined,

			item_to_show: null,

			headers: [
				{ align: 'center', sortable: false, value:'button' },
				{ text: 'Title', align: 'start', sortable: true, width: '200px', value:'title' },
				{ text: 'Submitted Date', align: 'center', sortable: true, value:'submitted_date' },
				{ text: 'Status Date', align: 'center', sortable: true, value:'status_date' },
				{ text: 'Status', align: 'center', sortable: true, value:'publish_status' },
				{ text: 'Author', align: 'center', sortable: false, value:'creator_name' },
				{ text: 'Subject', align: 'center', sortable: false, value:'subjects' },
				{ text: 'Grade', align: 'center', sortable: false, value:'grades' },
				{ text: 'Alignments', align: 'center', sortable: false, value:'alignments' },
			],
			search: {
				term: undefined,
				filters: {
					grade: undefined,
					subject: undefined,
					lps: undefined,
					users: undefined
				}
			},
			options: {
				groupBy: [],
				groupDesc: [],
				itemsPerPage: 100,
				multiSort: false,
				mustSort: false,
				page: 1,
				sortBy: [],
				sortDesc: [false]
			},
		}
	},

	computed: {
		...mapState(['user_info', 'site_config']),

		user_is_admin() {
			return this.user_info.system_role == 'admin' || this.user_info.admin_rights.includes('su')
		},

		tabs() {
			let tabs = [
				{
					key: 'lesson',
					text: 'Lessons',
				},
				{
					key: 'activity',
					text: `${this.site_config.sparkl_app_name} Activities`,
				},
				{
					key: 'resource',
					text: 'Resources',
				},
			]
			return tabs
		},

		filter_grades() {
			let arr = []

			this.$store.state.grades.forEach(value => {
				let text = value

				switch(value) {
					case 'PK':
						text = 'Pre-K'
						break
					case 'K':
						text = 'Kindergarten'
						break
					default:
						if (!isNaN(value * 1)) text = 'Grade ' + value
				}

				arr.push({value, text})
			})
			
			return arr
		},

		filter_subjects() {
			let arr = []

			Object.keys(this.$store.state.subjects).forEach(value => {
				arr.push({value, text: value})
			})
			
			return arr
		},

		filter_statuses() {
			let arr = [{value: 'all', text: '--- ALL ---'}]
			for (let key in window.publish_statuses) {
				arr.push({value: key, text: window.publish_statuses[key].text})
				// hack to not retrieve "extra" statuses
				if (key == 'published') break
			}
			return arr
		},

		lesson_editor_title_style() {
			// try to make sure lesson title fits in the space we have alloted; if it doesn't, we get an ugly extra scroll bar
			let s = U.get_max_font_size_for_block_width({size_hi:18, size_lo:12, width:700, val:this.lesson_being_edited.lesson_title})
			return `font-size:${s}px`
		},
	},

	watch: {
		'search.filters.status'() {
			if (this.search.filters.status == 'all') {
				this.search.filters.status = null
			}
		},
	},

	mounted() {
		vapp.asset_manager_component = this
	},

	methods: {
		show_status(item, type) {
			this.item_to_show = item
		},

		asset_saved($event) {
			if ($event.type == 'lesson') {
				// TODO: I think we have to do something to preserve *_showing values, using $event.edited_lesson
				this.item_to_show.asset = new Lesson($event.updated_lesson)
			} else {
				this.item_to_show.asset = new Resource($event.updated_resource)
			}
		},

		lesson_saved(lesson) {
			this.status.item = new Lesson(lesson.updated_lesson)
			if (lesson.flag === 'and_close'){
				this.lesson_being_edited =  undefined
			}
		},

		resource_saved(resource) {
			this.status.item = new Resource(resource)
			this.resource_being_edited = undefined
		},

		status_updated(item) {
			this.status.item = item
		},
	}
}
</script>

<style lang="scss" scoped>
.k-asset-manager {
	text-align: left;
	width: 100vw;
	display: flex;
	flex-direction: column;
	overflow: auto;
	background-color: rgba(255, 255, 255, 0.85);
    padding: 15px 80px 40px 80px;
    margin: 0 auto;

	.table-wrapper {
		position: relative;
		// height: calc(100vh - 430px);
	}
}

.k-search {
	&-group {
		width: 100%;
		justify-content: space-between;
		position: relative;
	}

	&-field {
		width: 18% !important;
	}
}

@media (max-width: 1280px) {
    .k-asset-manager {
        padding-left: 40px;
        padding-right: 40px;
    }
}
</style>

<style lang="scss">
.load-btn {
    position: absolute;
    right: 12px;
    top: 12px;
}

.v-application--is-ltr .v-data-table--fixed-header .v-data-footer {
	margin-right: unset !important;
}
</style>